// Custody Tier panel — the four-tier CMK model (standard / enhanced / premium /
// sovereign) with WebAuthn step-up for protected changes.
//
// Reading the current tier is open to any org member; changing it is org-admin
// only and, for a WebAuthn-protected tier, needs a security-key step-up. The
// backend is authoritative: we attempt the change, and only run the WebAuthn
// ceremony when the backend answers 403 "step-up required", retrying with the
// minted token. If no security key is enrolled yet we prompt to enrol first.

import { useState, useEffect, useCallback } from 'react'
import {
  Shield, ShieldCheck, KeyRound, Lock, Loader2, Check, X, Fingerprint, AlertTriangle,
} from 'lucide-react'
import { getCmkTier, updateCmkTier, type CmkTier } from '../../services/api'
import { enrolAuthenticator, stepUp, isWebAuthnAvailable, NO_AUTHENTICATOR } from '../../services/webauthnClient'

type TierId = CmkTier['tier']

interface TierMeta {
  name: string
  tagline: string
  icon: typeof Shield
  requires_webauthn: boolean
  allows_byok: boolean
  /** Roadmap = advertised direction, not a capability we can perform today. */
  roadmap?: boolean
  roadmapNote?: string
}

// Presentation metadata (the backend enforces the actual rules; this drives
// icons/labels/badges only). Order = escalation order.
//
// Story #4749 Stream 1.1 (RT-32 / RT-65) — what changed and why:
//   * The XKS badge and `allows_xks` are GONE. There is no external key store
//     in any form; org_keys/handler.py:500 already told customers so, while
//     this panel sold it as a Sovereign feature.
//   * Premium's tagline said "Bring your own AWS KMS key (BYOK)". That IS real
//     (register-key, deployed) — but the backend's matching feature list said
//     "bring your own key MATERIAL (BYOK import)", which is deployed in no
//     account and absent from terraform. The two were conflated, so the false
//     claim rode on the true one. Premium now names only the real capability.
//   * Sovereign is marked roadmap and cannot be selected. Both backends also
//     refuse it — hiding a control here is presentation, not a boundary.
const TIER_META: Record<TierId, TierMeta> = {
  standard: {
    name: 'Standard', tagline: 'Platform-managed keys, standard protection.',
    icon: Shield, requires_webauthn: false, allows_byok: false,
  },
  enhanced: {
    name: 'Enhanced', tagline: 'Security-key step-up required for key & tier changes.',
    icon: ShieldCheck, requires_webauthn: true, allows_byok: false,
  },
  premium: {
    name: 'Premium', tagline: 'Register your own AU-resident AWS KMS key + step-up.',
    icon: KeyRound, requires_webauthn: true, allows_byok: true,
  },
  sovereign: {
    name: 'Sovereign', tagline: 'External key store (XKS) — on the roadmap.',
    icon: Lock, requires_webauthn: true, allows_byok: true, roadmap: true,
    roadmapNote:
      'Not yet available. The strongest custody we can perform today is Premium: '
      + 'your own AU-resident AWS KMS key, security-key step-up on every custody '
      + 'change, and a revoke kill switch.',
  },
}
const TIER_ORDER: TierId[] = ['standard', 'enhanced', 'premium', 'sovereign']

function requiresWebAuthn403(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { requires_webauthn?: boolean } } }
  return e?.response?.status === 403 && e?.response?.data?.requires_webauthn === true
}
function errText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string }
  return e?.response?.data?.error || e?.message || fallback
}

export default function CustodyTierPanel() {
  const [tier, setTier] = useState<CmkTier | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [busy, setBusy] = useState<TierId | ''>('')
  const [enrolling, setEnrolling] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  // When a change needs a security key that isn't enrolled, remember the target
  // so the user can enrol then retry in one step.
  const [pendingTarget, setPendingTarget] = useState<TierId | ''>('')

  const load = useCallback(async () => {
    setLoading(true); setLoadError('')
    try {
      setTier(await getCmkTier())
    } catch (err: unknown) {
      setLoadError(errText(err, 'Failed to load your custody tier.'))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { load() }, [load])

  const applyChange = useCallback(async (target: TierId, token?: string) => {
    const updated = await updateCmkTier(target, token)
    setTier(updated)
    setSuccess(updated.message || `Custody tier changed to ${updated.name || target}.`)
    setPendingTarget('')
  }, [])

  const changeTier = useCallback(async (target: TierId) => {
    setError(''); setSuccess(''); setBusy(target); setPendingTarget('')
    try {
      // Optimistic: try without a step-up token. The backend answers 403 with
      // requires_webauthn when it needs one — then we run the ceremony and retry.
      try {
        await applyChange(target)
      } catch (err: unknown) {
        if (!requiresWebAuthn403(err)) throw err
        if (!isWebAuthnAvailable()) {
          setError('This change needs a security key, but this browser doesn’t support WebAuthn.')
          return
        }
        let token: string
        try {
          token = await stepUp('update-tier')
        } catch (se: unknown) {
          if ((se as Error).message === NO_AUTHENTICATOR) {
            setPendingTarget(target)
            setError('')
            return
          }
          // A cancelled (or timed-out) browser prompt throws NotAllowedError, whose own
          // text is a spec link — say plainly what happened and that nothing changed.
          if (/notallowed|cancel|timed out/i.test(`${(se as Error).name} ${(se as Error).message}`)) {
            setError('Security-key check cancelled — your custody tier was not changed.')
            return
          }
          throw se
        }
        await applyChange(target, token)
      }
    } catch (err: unknown) {
      setError(errText(err, 'Failed to change custody tier.'))
    } finally {
      setBusy('')
    }
  }, [applyChange])

  const enrol = useCallback(async (thenChangeTo?: TierId) => {
    setError(''); setSuccess(''); setEnrolling(true)
    try {
      await enrolAuthenticator()
      setSuccess('Security key enrolled.')
      if (thenChangeTo) await changeTier(thenChangeTo)
    } catch (err: unknown) {
      const msg = errText(err, 'Enrolment failed.')
      // A user cancelling the browser prompt throws NotAllowedError — soften it.
      setError(/notallowed|cancel/i.test(msg) ? 'Enrolment was cancelled.' : msg)
    } finally {
      setEnrolling(false)
    }
  }, [changeTier])

  // Only an org admin may change the tier; a member reads it. Absent (an older API) = keep the controls.
  const canChange = tier?.can_change !== false

  return (
    <div className="light-card p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Fingerprint size={18} className="text-iflow-500" />
          <h2 className="text-base font-semibold text-gray-900">Custody Tier</h2>
        </div>
        {tier && (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-iflow-100 text-iflow-700">
            {TIER_META[tier.tier]?.name || tier.name}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Your custody tier sets how strongly key operations are protected. Escalating to a
        protected tier requires a <b>security key</b> (WebAuthn) step-up, and is available to
        organisation admins.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : loadError ? (
        <p className="text-sm text-red-600">{loadError}</p>
      ) : tier ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {TIER_ORDER.map((id) => {
              const meta = TIER_META[id]
              const Icon = meta.icon
              const isCurrent = tier.tier === id
              const canGoHere = tier.available_transitions?.includes(id)
              return (
                <div
                  key={id}
                  className={`relative rounded-xl border-2 p-4 transition-colors ${
                    isCurrent ? 'border-iflow-500 bg-iflow-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={16} className={isCurrent ? 'text-iflow-600' : 'text-gray-500'} />
                    <span className="text-sm font-semibold text-gray-900">{meta.name}</span>
                    {isCurrent && (
                      <span className="ml-auto text-[10px] font-medium bg-iflow-700 text-white px-1.5 py-0.5 rounded-full">Current</span>
                    )}
                    {meta.roadmap && !isCurrent && (
                      <span className="ml-auto text-[10px] font-medium bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full">Roadmap</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{meta.tagline}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {meta.requires_webauthn && <Badge>Step-up</Badge>}
                    {meta.allows_byok && <Badge>BYOK</Badge>}
                    {!meta.requires_webauthn && !meta.allows_byok && <Badge>Baseline</Badge>}
                  </div>
                  {meta.roadmap && (
                    <p className="text-xs text-gray-500 mb-3">{meta.roadmapNote}</p>
                  )}
                  {!isCurrent && !meta.roadmap && canChange && (
                    <button
                      onClick={() => changeTier(id)}
                      disabled={!canGoHere || !!busy}
                      title={!canGoHere ? 'Not an available transition from your current tier' : undefined}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-iflow-500 text-iflow-700 rounded-lg hover:bg-iflow-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {busy === id ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                      {meta.requires_webauthn ? `Change to ${meta.name} (step-up)` : `Change to ${meta.name}`}
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          {!canChange && (
            <p className="text-xs text-gray-600 mb-3" data-testid="tier-admin-only">
              Only organisation admins can change the custody tier.
            </p>
          )}

          {/* Enrol-first prompt (shown when a protected change needs a key and none is enrolled) */}
          {canChange && pendingTarget && (
            <div className="mb-3 border-l-4 border-amber-400 bg-amber-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-900 mb-1">Enrol a security key to continue</p>
              <p className="text-sm text-amber-800 mb-3">
                Changing to <b>{TIER_META[pendingTarget].name}</b> needs a WebAuthn security key or passkey,
                and none is enrolled for this organisation yet. Enrol one now — you’ll then be asked to
                confirm the tier change with it.
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPendingTarget('')} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-white">Cancel</button>
                <button
                  onClick={() => enrol(pendingTarget)}
                  disabled={enrolling}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {enrolling && <Loader2 size={14} className="animate-spin" />} Enrol & continue
                </button>
              </div>
            </div>
          )}

          {/* Standalone enrol action */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-500">
              {isWebAuthnAvailable()
                ? 'Enrol a security key or passkey to protect key operations.'
                : 'This browser doesn’t support security keys (WebAuthn).'}
            </span>
            <button
              onClick={() => enrol()}
              disabled={enrolling || !isWebAuthnAvailable() || !!busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {enrolling ? <Loader2 size={14} className="animate-spin" /> : <Fingerprint size={14} />} Enrol a security key
            </button>
          </div>
        </>
      ) : null}

      {error && (
        <div className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X size={14} /></button>
        </div>
      )}
      {success && (
        <div className="mt-3 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <Check size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-emerald-400 hover:text-emerald-600"><X size={14} /></button>
        </div>
      )}
    </div>
  )
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
      {children}
    </span>
  )
}
